import { useEffect, useState } from "react";
import { ImSpinner2 } from "react-icons/im";

import {
  exportPriceListsAsPdf,
  getSkuBrands,
  getSkuCategories,
  getPriceListSkuNames,
  getAllSkuSubCategoriesNotPaginated,
} from "../../../../services/sku";
import {
  getAllActiveCarBrands,
  getCarModels,
  getBrandFamilies,
} from "../../../../services/car";
import Select from "../../../Select/Select";
import Modal from "../Modal";

const initialFilters = {
  skuBrandIdList: [],
  skuNameIdList: [],
  categoryIdList: [],
  skuSubCategoryIdList: [],
  carBrandIdList: [],
  carModelIdList: [],
  carBrandFamilyIdsList: [],
};

const groupByOptions = [
  { id: "carBrand", label: "Car brand" },
  { id: "skuBrand", label: "SKU brand" },
];

const uniqueById = (options) => [
  ...new Map(options.map((option) => [option.id, option])).values(),
];

const PriceListModal = ({ closeModal }) => {
  const [filters, setFilters] = useState(initialFilters);
  const [groupBy, setGroupBy] = useState();
  const [options, setOptions] = useState({
    skuBrands: [],
    skuNames: [],
    categories: [],
    subCategories: [],
    carBrands: [],
    carModels: [],
    carBrandFamilies: [],
  });
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [subCategoriesLoading, setSubCategoriesLoading] = useState(false);
  const [skuNamesLoading, setSkuNamesLoading] = useState(false);
  const [carModelsLoading, setCarModelsLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const updateFilter = (name) => (selectedOptions) => {
    setFilters((current) => ({
      ...current,
      [name]: selectedOptions,
      ...(name === "categoryIdList"
        ? { skuSubCategoryIdList: [], skuNameIdList: [] }
        : name === "skuSubCategoryIdList"
          ? { skuNameIdList: [] }
          : {}),
    }));
    if (name === "categoryIdList") {
      setOptions((current) => ({ ...current, subCategories: [], skuNames: [] }));
      setSubCategoriesLoading(Boolean(selectedOptions.length));
      setSkuNamesLoading(false);
    } else if (name === "skuSubCategoryIdList") {
      setOptions((current) => ({ ...current, skuNames: [] }));
      setSkuNamesLoading(Boolean(filters.categoryIdList.length && selectedOptions.length));
    }
    setError("");
  };

  useEffect(() => {
    let active = true;

    const loadOptions = async () => {
      setOptionsLoading(true);
      try {
        const [skuBrands, categories, carBrands, families] =
          await Promise.all([
            getSkuBrands({ hasStock: true }),
            getSkuCategories({ hasStock: true }),
            getAllActiveCarBrands({ hasStock: true }),
            getBrandFamilies({ hasStock: true }),
          ]);
        if (!active) return;

        setOptions((current) => ({
          ...current,
          skuBrands: skuBrands.data.data || [],
          categories: categories.data.data || [],
          carBrands: carBrands.data.carBrandsList || [],
          carBrandFamilies: (families.data.result || []).map((family) => ({
            ...family,
            brandFamilyLabel: `${family.id} | ${family.brandFamilyName}`,
          })),
        }));
      } catch (requestError) {
        if (active) {
          setError("Unable to load price list filters. Please try again.");
        }
      } finally {
        if (active) setOptionsLoading(false);
      }
    };

    loadOptions();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const categoryIds = filters.categoryIdList.map(({ id }) => id);

    if (!categoryIds.length) {
      setSubCategoriesLoading(false);
      return undefined;
    }

    const loadSubCategories = async () => {
      setSubCategoriesLoading(true);
      try {
        const response = await getAllSkuSubCategoriesNotPaginated({
          skuCategoryIdList: categoryIds.join(","),
          hasStock: true,
        });
        if (!active) return;

        setOptions((current) => ({
          ...current,
          subCategories: response.data || [],
        }));
      } catch (requestError) {
        if (active) setError("Unable to load SKU sub-categories.");
      } finally {
        if (active) setSubCategoriesLoading(false);
      }
    };

    loadSubCategories();
    return () => {
      active = false;
    };
  }, [filters.categoryIdList]);

  useEffect(() => {
    let active = true;
    if (!filters.categoryIdList.length || !filters.skuSubCategoryIdList.length) {
      setSkuNamesLoading(false);
      return undefined;
    }

    const loadSkuNames = async () => {
      setSkuNamesLoading(true);
      try {
        const response = await getPriceListSkuNames({
          hasStock: true,
          categoryIdList: filters.categoryIdList.map(({ id }) => id).join(","),
          skuSubCategoryIdList: filters.skuSubCategoryIdList.map(({ id }) => id).join(","),
        });
        if (!active) return;

        setOptions((current) => ({ ...current, skuNames: response.data.data || [] }));
      } catch (requestError) {
        if (active) setError("Unable to load SKU names.");
      } finally {
        if (active) setSkuNamesLoading(false);
      }
    };

    loadSkuNames();
    return () => {
      active = false;
    };
  }, [filters.categoryIdList, filters.skuSubCategoryIdList]);

  useEffect(() => {
    let active = true;
    const brandIds = filters.carBrandIdList.map(({ id }) => id);

    if (!brandIds.length) {
      setOptions((current) => ({ ...current, carModels: [] }));
      setFilters((current) => ({ ...current, carModelIdList: [] }));
      return undefined;
    }

    const loadCarModels = async () => {
      setCarModelsLoading(true);
      try {
        const response = await getCarModels({
          carBrandIdList: brandIds.join(","),
          hasStock: true,
        });
        if (!active) return;

        const carModels = uniqueById(response.data.carModelsList || []);
        const availableIds = new Set(carModels.map(({ id }) => id));
        setOptions((current) => ({ ...current, carModels }));
        setFilters((current) => ({
          ...current,
          carModelIdList: current.carModelIdList.filter(({ id }) =>
            availableIds.has(id)
          ),
        }));
      } catch (requestError) {
        if (active) setError("Unable to load car models.");
      } finally {
        if (active) setCarModelsLoading(false);
      }
    };

    loadCarModels();
    return () => {
      active = false;
    };
  }, [filters.carBrandIdList]);

  const hasFilters = Object.values(filters).some((selected) => selected.length);

  const downloadPriceList = async (event) => {
    event.preventDefault();
    if (!hasFilters || downloading) return;

    const params = Object.entries(filters).reduce(
      (result, [name, selected]) => {
        if (selected.length) {
          result[name] = selected.map(({ id }) => id).join(",");
        }
        return result;
      },
      {}
    );
    if (groupBy) params.groupBy = groupBy.id;

    setDownloading(true);
    setError("");
    try {
      const response = await exportPriceListsAsPdf(params);
      const { pdfBuffer } = response.data;
      if (!pdfBuffer) {
        setError("No results. Please select at least one filter.");
        return;
      }

      const byteCharacters = atob(pdfBuffer);
      const byteNumbers = Array.from(byteCharacters, (character) =>
        character.charCodeAt(0)
      );
      const blob = new Blob([new Uint8Array(byteNumbers)], {
        type: "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "price-list.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      closeModal();
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
          "Unable to download the price list. Please try again."
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal close={closeModal} classNames="lg:w-2/3 sm:w-2/3 top-4">
      <form dir="rtl" onSubmit={downloadPriceList}>
        <h3 dir="ltr" className="mb-6 text-center">Price List</h3>
        <div dir="ltr" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div dir="ltr" className="text-left" data-testid="sku-categories-field">
            <label>SKU categories</label>
            <Select direction="ltr" optionsDirection="rtl" placeholder="Select categories" options={options.categories} optionLabelProp="categoryName" onSelect={updateFilter("categoryIdList")} initialValue={filters.categoryIdList} isMulti isLoading={optionsLoading} />
          </div>
          <div dir="ltr" className="text-left">
            <label>Car brand families</label>
            <Select direction="ltr" optionsDirection="rtl" placeholder="Select brand families" options={options.carBrandFamilies} optionLabelProp="brandFamilyLabel" onSelect={updateFilter("carBrandFamilyIdsList")} initialValue={filters.carBrandFamilyIdsList} isMulti isLoading={optionsLoading} />
          </div>
          <div dir="ltr" className="text-left" data-testid="sku-sub-categories-field">
            <label>SKU sub-categories</label>
            <Select direction="ltr" optionsDirection="rtl" placeholder="Select sub-categories" options={options.subCategories} optionLabelProp="subCategoryName" onSelect={updateFilter("skuSubCategoryIdList")} initialValue={filters.skuSubCategoryIdList} isMulti isDisabled={optionsLoading || subCategoriesLoading || !filters.categoryIdList.length} isLoading={subCategoriesLoading} />
          </div>
          <div dir="ltr" className="text-left" data-testid="car-brands-field">
            <label>Car brands</label>
            <Select direction="ltr" optionsDirection="rtl" placeholder="Select car brands" options={options.carBrands} optionLabelProp="brandName" onSelect={updateFilter("carBrandIdList")} initialValue={filters.carBrandIdList} isMulti isLoading={optionsLoading} />
          </div>
          <div dir="ltr" className="text-left">
            <label>SKU names</label>
            <Select direction="ltr" optionsDirection="rtl" placeholder="Select SKU names" options={options.skuNames} optionLabelProp="skuName" onSelect={updateFilter("skuNameIdList")} initialValue={filters.skuNameIdList} isMulti isDisabled={optionsLoading || subCategoriesLoading || skuNamesLoading || !filters.categoryIdList.length || !filters.skuSubCategoryIdList.length} isLoading={skuNamesLoading} />
          </div>
          <div dir="ltr" className="text-left" data-testid="car-models-field">
            <label>Car models</label>
            <Select direction="ltr" optionsDirection="rtl" placeholder="Select car models" options={options.carModels} optionLabelProp="modelName" onSelect={updateFilter("carModelIdList")} initialValue={filters.carModelIdList} isMulti isDisabled={optionsLoading || carModelsLoading || !filters.carBrandIdList.length} isLoading={carModelsLoading} />
          </div>
          <div dir="ltr" className="text-left">
            <label>SKU brands</label>
            <Select direction="ltr" optionsDirection="rtl" placeholder="Select SKU brands" options={options.skuBrands} optionLabelProp="brandName" onSelect={updateFilter("skuBrandIdList")} initialValue={filters.skuBrandIdList} isMulti isLoading={optionsLoading} />
          </div>
          <div dir="ltr" className="text-left">
            <label>Group by</label>
            <Select direction="ltr" optionsDirection="rtl" placeholder="No grouping" options={groupByOptions} optionLabelProp="label" onSelect={setGroupBy} initialValue={groupBy?.label} />
          </div>
        </div>

        {error && <p dir="ltr" className="mt-4 text-left text-red-500">{error}</p>}

        <div dir="ltr" data-testid="price-list-actions" className="flex justify-end gap-3 mt-6">
          <button type="button" className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg" onClick={closeModal} disabled={downloading}>Cancel</button>
          <button type="submit" className="bg-dark-blue text-white px-6 py-2 rounded-lg disabled:bg-light-gray disabled:cursor-not-allowed flex items-center gap-2" disabled={!hasFilters || downloading}>
            {downloading && <ImSpinner2 className="animate-spin" />}
            {downloading ? "Downloading..." : "Download"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default PriceListModal;
